import { SelectOptionItemType } from 'components/SelectOption'
import { Bank } from 'data/Payments/BankTransfer/types'
import { BeneficiaryType } from 'data/types'
import uuidv4 from 'uuid/v4'

export type ErrorType = {
  readonly cause?: string
  readonly code?: number
  readonly status?: number
}

export type RemoteType<T> = {
  readonly error?: ErrorType
  readonly loading: boolean
  readonly value?: T
}

type RegionType = 'EEA'
type ScopeType = 'KYC' | 'MERCURY'

export type CountryType = {
  code: string
  name: string
  regions: Array<RegionType>
  scopes: Array<ScopeType>
  states: Array<string>
}

export type CountryCodeType = 'US' | 'GB' | 'DEFAULT' | string

export type FormattedCountryType = SelectOptionItemType & {
  readonly available: boolean
}

export type UsStateType = {
  readonly code: string
  readonly countryCode: 'US'
  readonly name: string
  readonly scopes: Array<ScopeType>
}

export type CommunicationType = {
  readonly created: string
  readonly details: string
  readonly id: string
  readonly message: string
  readonly type: string
}

export type ExpectedVolType = 'BELOW_10K' | 'BELOW_50K' | 'BELOW_100K' | 'BELOW_250K' | 'ABOVE_250K'

export type SourceOfFundsType =
  | 'SALARY'
  | 'CRYPTO_TRADING'
  | 'CRYPTO_MINING'
  | 'INVESTMENT_INCOME'
  | 'REAL_ESTATE'
  | 'INHERITANCE'

export type HighRiskQuestionnaireType = {
  readonly exchangePurpose: Array<string>
  readonly expectedVolume: void | ExpectedVolType
  readonly product: 'SWAP' | 'MERCURY'
  readonly sourceOfFunds: void | SourceOfFundsType
}

export type VeriffMetadataType = {
  readonly applicantId: string
  readonly data: {
    readonly baseUrl: string
    readonly token: string
    readonly url: string
  }
  readonly token: string
}

export type WidgetType = 'openOrders' | 'orderBookAndTrades' | 'priceHistory'

export type ModalNameType =
  | 'API_KEY_ACTIVATE'
  | 'API_KEY_ACTIVATE_FAIL'
  | 'API_KEY_CREATE'
  | 'API_KEY_DELETE'
  | 'API_KEY_SUCCESS'
  | 'CHANGE_EMAIL'
  | 'CHANGE_PASSWORD'
  | 'CLOSE_MARGIN'
  | 'DEPOSIT'
  | 'LANGUAGE_SELECTION'
  | 'LINK_WALLET'
  | 'MARGIN_ONBOARDING'
  | 'ORDER_CONFIRMATION'
  | 'ORDER_DETAILS'
  | 'RESET_PASSWORD'
  | 'RESUME_EMAIL_VERIFICATION'
  | 'SIGNIN'
  | 'SIGNUP'
  | 'SIGNOUT_SESSIONS'
  | 'SIGNUP_CONFIRM_BUY'
  | 'TIER_TWO'
  | 'ENABLE_TWO_FACTOR'
  | 'ENABLE_WHITELIST_ADDRESS'
  | 'DISABLE_TWO_FACTOR'
  | 'BACKUP_CODES_TWO_FACTOR'
  | 'VERIFICATION_DEPOSIT'
  | 'WITHDRAWAL'
  | 'WHATS_NEW'
  | 'MARGIN_WAITLIST'
  | 'CLOUT_RENAME'
  | 'NEW_MARGIN_PAIR'
  | 'TERMS_AND_CONDITIONS'
  | 'WIRE_INSTRUCTIONS'

export type NotificationTitleType =
  | 'KO_CHANGE_PASSWORD'
  | 'KO_GENERIC'
  | 'KO_ORDER_CANCELLED'
  | 'KO_ORDER_EXPIRED'
  | 'KO_PAYMENTS_REMOVE_BENEFICIARIES'
  | 'KO_PAYMENTS_REMOVE_WHITELIST'
  | 'KO_SIGNUP'
  | 'KO_TIER_TWO'
  | 'KO_TWO_FACTOR'
  | 'KO_USER_NOT_FOUND'
  | 'KO_VERIFY_TOTP'
  | 'KYC_VERIFIED'
  | 'KYC_UNDER_REVIEW'
  | 'OK_API_KEY_ACTIVATION'
  | 'OK_CHANGE_PASSWORD'
  | 'OK_COPY'
  | 'OK_EMAIL_NOT_VERIFIED'
  | 'OK_EMAIL_VERIFIED'
  | 'OK_ORDER_CREATED'
  | 'OK_ORDER_FILLED'
  | 'OK_ORDER_PARTIALLY_FILLED'
  | 'OK_ORDER_PENDING'
  | 'OK_REQUEST_HISTORY'
  | 'OK_RESET_PASSWORD'
  | 'OK_SAVE_PREFERENCES'
  | 'OK_SIGNOUT'
  | 'OK_SIMPLE_TRADE_ORDER'
  | 'OK_VERIFY_TOTP'
  | 'OK_WHITELIST_ADDED'
  | 'OK_WHITELIST_REMOVED'
  | 'OK_WHITELIST_DISABLED'

export enum ModalOrigin {
  BALANCE = 'BALANCE',
  DEPOSIT = 'DEPOSIT',
  EDD = 'EDD',
  LIMITS = 'LIMITS',
  LINK_BANK = 'LINK_BANK',
  LINK_WALLET = 'LINK_WALLET',
  NO_FUNDS = 'NO_FUNDS',
  ONBOARDING = 'ONBOARDING',
  PORTFOLIO = 'PORTFOLIO',
  SIGNUP = 'SIGNUP',
  UNKNOWN = 'UNKNOWN'
}

export type ModalOptionsType = {
  bank?: Bank
  beneficiary?: BeneficiaryType
  crypto?: string
  currency?: string
  currencyFrom?: string
  extra?: any
  fullscreen?: boolean
  id?: string
  origin?: ModalOrigin
  pair?: string
}

export type ModalType = {
  readonly isDismissable: boolean
  readonly name?: ModalNameType
  readonly options: ModalOptionsType
}

export type NotificationIdType = string

export type NotificationDuration = 0 | 2 | 5 | 10 | 20

type InternalNotifType = {
  readonly created: number
  readonly descValue?: string
  readonly description?: string
  readonly duration: NotificationDuration
  readonly subtext?: string
  readonly title: NotificationTitleType
  readonly type: 'danger' | 'default' | 'success' | 'warning'
}

type TitleType = {
  readonly title: NotificationTitleType
}

export type RawNotificationType = InternalNotifType & TitleType

export type NotificationType = {
  readonly id: NotificationIdType
} & InternalNotifType &
  TitleType

export const generateNotificationId: () => NotificationIdType = () => uuidv4()
